import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import { PostIllustration } from './BlogPage';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function LeafIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.54A1 1 0 005.2 20.9C6.24 19.29 7.71 18 9 18c1.5 0 3 1 5 1 5 0 6-4 6-6V8c-.6.2-2 0-3 0z"/>
    </svg>
  );
}

function renderContent(text, dark) {
  return text.trim().split('\n').map((line, i) => {
    if (line.startsWith('## ')) {
      return (
        <h2 key={i} className={`font-serif text-2xl font-bold mt-10 mb-4 ${dark ? 'text-white' : 'text-green-900'}`}>
          {line.replace('## ', '')}
        </h2>
      );
    }
    if (line.trim() === '') return <div key={i} className="h-3" />;
    return (
      <p key={i} className={`text-base leading-relaxed ${dark ? 'text-green-200/75' : 'text-green-800/80'}`}>
        {line}
      </p>
    );
  });
}

export default function BlogPostPage({ dark }) {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!post) {
    return (
      <div className={`min-h-screen pt-[70px] flex flex-col items-center justify-center text-center px-6 ${dark ? 'bg-[#0d1a0f]' : 'bg-white'}`}>
        <LeafIcon className={`w-12 h-12 mb-4 ${dark ? 'text-green-700' : 'text-green-200'}`} />
        <h1 className={`font-serif text-3xl font-bold mb-3 ${dark ? 'text-white' : 'text-green-900'}`}>
          Article not found
        </h1>
        <p className={`text-sm mb-6 ${dark ? 'text-green-500' : 'text-green-500'}`}>
          The article you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/blog"
          className="bg-green-700 hover:bg-green-800 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all duration-200"
        >
          ← Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-[70px] ${dark ? 'bg-[#0d1a0f]' : 'bg-white'}`}>
      <div className="max-w-3xl mx-auto px-6 py-16">

        {/* Breadcrumb / Back */}
        <nav className="flex items-center gap-2 text-sm mb-10">
          <Link
            to="/blog"
            className={`flex items-center gap-1.5 font-medium transition-colors group ${dark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'}`}
          >
            <span className="group-hover:-translate-x-1 transition-transform inline-block">←</span>
            Back to Blog
          </Link>
          <span className={dark ? 'text-green-700' : 'text-green-300'}>/</span>
          <span className={`truncate max-w-xs ${dark ? 'text-green-600' : 'text-green-400'}`}>{post.title}</span>
        </nav>

        {/* Category + meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${dark ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-800'}`}>
            {post.category}
          </span>
          <span className={`text-xs ${dark ? 'text-green-500' : 'text-green-500'}`}>{formatDate(post.date)}</span>
          <span className={`text-xs ${dark ? 'text-green-500' : 'text-green-500'}`}>· {post.readTime}</span>
        </div>

        {/* Title */}
        <h1 className={`font-serif text-3xl md:text-4xl font-bold leading-tight mb-6 ${dark ? 'text-white' : 'text-green-900'}`}>
          {post.title}
        </h1>

        {/* Author row */}
        <div className={`flex items-center gap-3 pb-8 mb-8 border-b ${dark ? 'border-green-400/10' : 'border-green-800/10'}`}>
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${dark ? 'bg-green-700' : 'bg-green-100'}`}>
            <LeafIcon className={`w-4 h-4 ${dark ? 'text-green-300' : 'text-green-700'}`} />
          </div>
          <div>
            <p className={`text-sm font-medium ${dark ? 'text-green-300' : 'text-green-700'}`}>{post.author}</p>
            <p className={`text-xs ${dark ? 'text-green-600' : 'text-green-500'}`}>GEA Farm Ltd</p>
          </div>
        </div>

        {/* Illustration */}
        <div className={`rounded-2xl h-56 flex items-center justify-center mb-10 ${dark ? 'bg-green-900/30' : 'bg-green-50'}`}>
          <PostIllustration category={post.category} dark={dark} />
        </div>

        {/* Body */}
        <div className="space-y-2">
          {renderContent(post.content, dark)}
        </div>

        {/* CTA */}
        <div className={`mt-16 rounded-2xl p-8 text-center border ${dark ? 'bg-green-900/30 border-green-400/10' : 'bg-green-50 border-green-800/10'}`}>
          <p className={`font-serif text-xl font-bold mb-2 ${dark ? 'text-white' : 'text-green-900'}`}>
            Interested in our farm?
          </p>
          <p className={`text-sm mb-5 ${dark ? 'text-green-400' : 'text-green-600'}`}>
            Learn more about GEA Farm's sustainable farming operations and produce.
          </p>
          <Link
            to="/blog"
            className="bg-green-700 hover:bg-green-800 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all duration-200 hover:-translate-y-0.5 inline-block"
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    </div>
  );
}
